import React, { useState } from 'react';
import { Plus, Search, Edit } from 'lucide-react';
// @ts-ignore
import DraggableTable from '../../components/DraggableTable';
import RightPanel from '../../components/RightPanel';
import LoadingCard from '../../components/LoadingCard';
import Pagination from '../../components/ui/Pagination';
import { showSuccess } from '../../utils/toast';
import { customersApi } from '../../services/api';
import { emptyForm, emptyContactForm } from './utils/customerHelpers';

import { useCustomers } from './hooks/useCustomers';
import { useContacts } from '../customer-contacts/hooks/useContacts';
import { useCustomerPanel } from './hooks/useCustomerPanel';
import { useHighlight } from '../../hooks/useHighlight';

import { CUSTOMER_COLUMNS } from './components/customerColumns';
import CustomerCreateModal from './components/CustomerCreateModal';
import CustomerViewPanel from './components/CustomerViewPanel';
import CustomerEditForm from './components/CustomerEditForm';

const CustomersPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);

  const {
    customers,
    loading,
    error,
    setError,
    searchQuery,
    setSearchQuery,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    total,
    load,
    handlePageChange,
  } = useCustomers();

  const { highlightedId } = useHighlight(customers as any, 'data-customer-id');

  const {
    contacts,
    loadingContacts,
    showContactForm,
    setShowContactForm,
    editingContact,
    setEditingContact,
    contactForm,
    setContactForm,
    submittingContact,
    loadContacts,
    resetContactState,
    handleContactFormChange,
    handleSubmitContact,
    handleEditContact,
    handleDeleteContact,
  } = useContacts();

  const {
    selectedCustomer,
    viewMode,
    setViewMode,
    submitting: panelSubmitting,
    handleViewDetails,
    handleEdit,
    handleClosePanel,
    handleCustomerChange,
    handleUpdateCustomer,
  } = useCustomerPanel({ loadContacts, resetContactState, load, setError });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await customersApi.create(form);
      setForm({ ...emptyForm });
      setShowForm(false);
      await load();
      showSuccess('Client created successfully!');
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="theme-card rounded-2xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold theme-text-primary">ClientTRAC</h2>
        <button
          type="button"
          onClick={() => {
            setShowForm((v) => !v);
            setError(null);
            if (showForm) setForm({ ...emptyForm });
          }}
          className="flex items-center justify-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-shadow text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          {showForm ? 'Close Form' : 'Add Client'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <CustomerCreateModal
        showForm={showForm}
        form={form}
        submitting={submitting}
        error={error}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowForm(false);
          setForm({ ...emptyForm });
          setError(null);
        }}
      />

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-tertiary w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, ID, short name..."
            className="w-full pl-10 pr-4 py-2 theme-border rounded-lg border border-white  theme-border rounded-lg p-2  focus:outline-none 
         focus:border-blue-500 
  focus:ring-0  theme-bg-primary theme-text-primary  border border-white theme-bg-primary theme-text-primary"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-4">
          <LoadingCard title="ClientTRAC" message="Loading customers..." />
        </div>
      ) : (
        <>
          <DraggableTable
            columns={CUSTOMER_COLUMNS(
              highlightedId,
              false,
              handleViewDetails,
              handleEdit,
            )}
            data={customers}
            emptyMessage={searchQuery ? 'No customers found matching your search' : 'No customers yet'}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            startIndex={(currentPage - 1) * itemsPerPage}
            endIndex={currentPage * itemsPerPage}
            total={total}
            loading={loading}
            onPageChange={handlePageChange}
            onItemsPerPageChange={(perPage) => {
              setItemsPerPage(perPage);
              setCurrentPage(1);
            }}
          />
        </>
      )}

      {/* Right Side Panel */}
      <RightPanel
        isOpen={!!selectedCustomer && !!viewMode}
        onClose={handleClosePanel}
        title={viewMode === 'view' ? 'Customer Details' : 'Edit Customer'}
        widthClass="w-full md:w-1/2"
        headerExtras={
          viewMode === 'view' && selectedCustomer ? (
            <button
              onClick={() => setViewMode('edit')}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
              title="Edit customer"
            >
              <Edit className="w-3.5 h-3.5" />
              Edit
            </button>
          ) : undefined
        }
      >
        {selectedCustomer && viewMode === 'view' && (
          <CustomerViewPanel
            selectedCustomer={selectedCustomer}
            contacts={contacts}
            loadingContacts={loadingContacts}
            showContactForm={showContactForm}
            editingContact={editingContact}
            contactForm={contactForm}
            submittingContact={submittingContact}
            onContactFormChange={handleContactFormChange}
            onSubmitContact={(e) => handleSubmitContact(e, selectedCustomer.id)}
            onEditContact={handleEditContact}
            onDeleteContact={(contactId) => handleDeleteContact(contactId, selectedCustomer.id)}
            onAddContactClick={() => {
              setEditingContact(null);
              setContactForm({ ...emptyContactForm });
              setShowContactForm(true);
            }}
            onCancelContactForm={() => {
              setShowContactForm(false);
              setEditingContact(null);
              setContactForm({ ...emptyContactForm });
            }}
          />
        )}

        {selectedCustomer && viewMode === 'edit' && (
          <CustomerEditForm
            selectedCustomer={selectedCustomer}
            submitting={panelSubmitting}
            error={error}
            onChange={handleCustomerChange}
            onSubmit={handleUpdateCustomer}
            onCancel={() => setViewMode('view')}
          />
        )}
      </RightPanel>
    </div>
  );
};

export default CustomersPage;
